import { useRef } from 'react';
import { StanButton, StanCheckbox, StanLink, StanProgressBar } from '@/components';
import {
    StripePaymentInput,
    StripePaymentInputRef,
} from '@/components/third-party/StripePaymentInput/StripePaymentInput';
import {
    ExpressCheckoutInput,
    ExpressCheckoutInputRef,
    useExpressCheckout,
} from '@/components/third-party/ExpressCheckoutInput';
import { useSubscriptionCreation, useSubscriptionFlags } from '@/features/subscriptions/hooks';
import { DuplicatePaymentModal } from '@/features/subscriptions/components/DuplicatePaymentModal';
import { getProgressPercentage } from '@/features/subscriptions/utils/subscriptionFlags';
import { useSessionCountry } from '@/features/auth/hooks/useSessionCountry';
import { getGDPRComplianceDocument } from '@/features/auth/utils/gdpr';
import { StanLoader } from '@/components/core-components/StanLoader/StanLoader';
import { getDateXDaysFromNow } from '@/lib/date';
import { LEGAL_URLS } from '@/lib/constants';

const SubscribePaymentPage = () => {
    const flags = useSubscriptionFlags();
    const { country } = useSessionCountry();
    const stripeRef = useRef<StripePaymentInputRef>(null);
    const expressCheckoutRef = useRef<ExpressCheckoutInputRef>(null);
    const { confirmExpressCheckout } = useExpressCheckout();

    const {
        isLoading,
        isSubmitting,
        termsAgreed,
        setTermsAgreed,
        subscriptionState,
        hasPastSubscription,
        handleSubmit,
        handleExpressCheckout,
        duplicatePaymentCheck,
    } = useSubscriptionCreation();

    const progressPercentage = getProgressPercentage('payment', !!flags.add_socials_before_subscription);
    const gdprDocument = getGDPRComplianceDocument(country);
    const trialEndDate = getDateXDaysFromNow(subscriptionState.trialDays);

    // Determine if this is a free subscription (no credit card required = 100% off promo)
    const isFreeSubscription = !subscriptionState.creditCardRequired;

    const handleFormSubmit = () => {
        handleSubmit(stripeRef);
    };

    const handleExpressConfirm = () => {
        const elements = expressCheckoutRef.current?.getElements();
        if (elements) {
            handleExpressCheckout(elements, confirmExpressCheckout);
        }
    };

    if (isLoading) {
        return (
            <div className='flex min-h-svh w-full items-center justify-center'>
                <StanLoader className='flex items-center' animationDuration={1000} dotSize={18} dotsNum={3} />
            </div>
        );
    }

    return (
        <div className='flex h-full min-h-[calc(100svh-4rem)] w-full max-w-md flex-col'>
            {/* Progress Bar */}
            <div className='mx-auto w-1/2 pb-6'>
                <StanProgressBar
                    value={progressPercentage}
                    min={0}
                    max={100}
                    progressColor='#a286ff'
                    barColor='#eef1f5'
                />
            </div>

            {/* Main Container */}
            <div className='mx-auto flex w-full flex-1 flex-col gap-5 px-8'>
                {/* Info Note - Different messaging for free subscription vs trial */}
                {!hasPastSubscription && (
                    <div className='bg-brand-subtle mt-2.5 w-full rounded-lg px-2 py-5 text-center'>
                        {isFreeSubscription ? (
                            <>
                                <p className='text-body-md text-dark'>Your free subscription is ready! 🎉</p>
                                <p className='text-body-sm text-dark mt-1'>
                                    Just agree to the terms below to get started
                                </p>
                            </>
                        ) : (
                            <>
                                <p className='text-body-md text-dark'>Your store runs free till {trialEndDate}</p>
                                <p className='text-body-sm text-dark mt-1'>Cancel anytime 💝</p>
                            </>
                        )}
                    </div>
                )}

                {/* Payment Method Section */}
                <div>
                    {subscriptionState.creditCardRequired && (
                        <>
                            <h3 className='text-dark mb-2.5'>Payment Method</h3>
                            <ExpressCheckoutInput ref={expressCheckoutRef} onConfirm={handleExpressConfirm} />
                            <StripePaymentInput ref={stripeRef} />
                        </>
                    )}
                </div>

                {/* Submit Button */}
                <div className='mt-auto mb-4'>
                    {/* Terms of Service Checkbox */}
                    <div className='mt-5 mb-5'>
                        <div className='flex items-start gap-2'>
                            <StanCheckbox
                                id='terms-checkbox'
                                checked={termsAgreed}
                                onChange={e => setTermsAgreed(e.target.checked)}
                            />
                            <label
                                htmlFor='terms-checkbox'
                                className='text-body-xs text-dark cursor-pointer font-medium text-balance'
                            >
                                I have read and understand the{' '}
                                <a
                                    href={LEGAL_URLS.TERMS_OF_SERVICE}
                                    target='_blank'
                                    rel='noopener noreferrer'
                                    className='text-brand-main underline focus-visible:ring-2'
                                >
                                    Terms of Service
                                </a>{' '}
                                and{' '}
                                <a
                                    href={LEGAL_URLS.PRIVACY_POLICY}
                                    target='_blank'
                                    rel='noopener noreferrer'
                                    className='text-brand-main underline focus-visible:ring-2'
                                >
                                    Privacy Policy
                                </a>
                                {gdprDocument && (
                                    <>
                                        {'. '}Our GDPR compliance{' '}
                                        <a
                                            href={gdprDocument}
                                            target='_blank'
                                            rel='noopener noreferrer'
                                            className='text-brand-main underline focus-visible:ring-2'
                                        >
                                            certificate
                                        </a>{' '}
                                        is available for review.
                                    </>
                                )}
                            </label>
                        </div>
                    </div>

                    <StanButton
                        name='subscribe-button'
                        type='button'
                        color='primary'
                        loading={isSubmitting}
                        disabled={!termsAgreed}
                        onClick={handleFormSubmit}
                        className='w-full'
                    >
                        {hasPastSubscription
                            ? 'Subscribe'
                            : isFreeSubscription
                              ? 'Get Started'
                              : `Start Your ${subscriptionState.trialDays} Day Free Trial`}
                    </StanButton>

                    {hasPastSubscription && (
                        <StanLink
                            to='/dashboard'
                            className='text-body-sm text-brand-main mx-auto mt-4 block text-center font-medium underline underline-offset-2 focus-visible:ring-2'
                        >
                            Dashboard
                        </StanLink>
                    )}
                </div>
            </div>

            {/* Duplicate Payment Method Modal */}
            <DuplicatePaymentModal
                isOpen={duplicatePaymentCheck.isModalOpen}
                maskedEmail={duplicatePaymentCheck.maskedEmail}
                trialDays={subscriptionState.trialDays}
                onConfirm={duplicatePaymentCheck.handleConfirm}
                onGoToLogin={duplicatePaymentCheck.handleGoToLogin}
            />
        </div>
    );
};

export default SubscribePaymentPage;
